//======================================================================================================
//
// This store will house the hierarchy state and be updated via view change, site changes, and control panel updates.
//
//======================================================================================================

import { HierarchySelector } from "@/components/ssa_components/hierarchy/HierarchySelectorHelper";
import {
	DatasetObject,
	DatasetType,
	IDatasetDocument,
	IGetUiHierarchyResponseDTO
} from "@/types/dataset";

import {
	BLASTDOG_DATASET_NAME,
	CollarDrillholeLayerConfigType,
	NonDrillholeLayerConfigType
} from "@/utils/DisplayPaneConstants";
import i18n from "@/utils/i18n";
import { defineStore } from "pinia";
import { useApiStore as api } from "./apiStore";
import { useSiteStore as siteStore } from "./siteStore";
import { usePvwStore } from "./ssaStore/pvw";
import { useViewStore } from "./ssaStore/views";
import { useToastStore as toast } from "./toastStore";
export const useHierarchyStore = defineStore("hierarchyStore", {
	state: () =>
		({
			hierarchy: [],
			hierarchyStates: {},
			hierarchyLayerSelections: {}
		}) as {
			hierarchy: IGetUiHierarchyResponseDTO[];
			hierarchyStates: any;
			hierarchyLayerSelections: any;
		},
	getters: {
		HIERARCHY_STATE(): any {
			return this.hierarchyStates;
		},
		HIERARCHY_LAYER_SELECTIONS(): any {
			return this.hierarchyLayerSelections;
		}
	},
	actions: {
		async SET_HIERARCHY_STATE(hierarchyState: any, hierarchyLayerSelections: any) {
			this.hierarchyStates = hierarchyState;
			this.hierarchyLayerSelections = hierarchyLayerSelections;
		},
		async REMOVE_HIERARCHY_STATE(viewId: any) {
			const hState = this.hierarchyStates;
			const hLayerSelections = this.hierarchyLayerSelections;
			delete hState[viewId];
			delete hLayerSelections[viewId];
			this.hierarchyStates = hState;
			this.hierarchyLayerSelections = hLayerSelections;
		},
		async DATA_SELECT(viewId: string, selected: any) {
			if (useViewStore().viewIds.includes(viewId)) {
				this.hierarchyStates[viewId] = { selected };
			}
		},
		async HIERARCHY_LAYER_SET(viewId: string, value: any) {
			if (useViewStore().viewIds.includes(viewId)) {
				this.hierarchyLayerSelections[viewId] = value;
			}
		},
		async SSA_CONFIGURE_PARAVIEW() {
			try {
				if (this.hierarchy.length == 0) {
					if ((siteStore() as any).activeSite == null) {
						return;
					}

					const hierarchyQuery = `/dataset/hierarchy?site=${
						(siteStore() as any).activeSite.id
					}`;
					const { data } = await api().get(hierarchyQuery);

					if (data.length > 0 && data[0].paraviewConfig) {
						return await usePvwStore().PVW_CONFIGURE_MINE(data[0].paraviewConfig);
					} else {
						// throw new Error(); (if the paraview config doesn't exist & we aren't searching for a result with no results)
					}
				} else {
					if (this.hierarchy[0].paraviewConfig) {
						return await usePvwStore().PVW_CONFIGURE_MINE(
							this.hierarchy[0].paraviewConfig
						);
					} else {
						// throw new Error(); (if the paraview config doesn't exist & we aren't searching for a result with no results)
					}
				}
			} catch (e) {
				console.log("cant find configuration");
				toast().toast({
					message: "",
					title: i18n.global.t("storeHierarchyToasts.errorSettingConfig"),
					type: "error",
					dismissable: false
				});
			}
		},
		ALL_DATASETS() {
			const datasets: DatasetObject[] = [];

			const getAllDatasets = (
				children: IGetUiHierarchyResponseDTO[],
				datasetType: DatasetType = null
			) => {
				for (const child of children) {
					let currentDatasetType = datasetType;
					if (child.levelType == "datasetType") {
						currentDatasetType = child.name as DatasetType;
					}
					if (child.datasets) {
						for (const dataset of child.datasets) {
							datasets.push({
								...dataset,
								dataType: currentDatasetType,
								groupName: child.groupName
							});
						}
					}
					getAllDatasets(
						child.children.map((item: any) => ({
							...item,
							groupName: child.groupName
						})),
						currentDatasetType
					);
				}
			};

			getAllDatasets(this.hierarchy.map((item: any) => ({ ...item, groupName: item.name })));

			return datasets;
		},
		async fetchHierarchy(searchTerm = "") {
			try {
				let hierarchyQuery = `/dataset/hierarchy?site=${
					(siteStore() as any).activeSite.id
				}`;

				if (searchTerm) {
					hierarchyQuery += `&searchTerm=${searchTerm}`;
				}
				const { data } = await api().get(hierarchyQuery);

				this.checkForNewBlastdogSelections(data, this.hierarchy); // deselect any newly imported blastdog hierarchies
				this.hierarchy = [...data];
				if (data.length > 0) {
					await this.SSA_CONFIGURE_PARAVIEW();
					// await pvw.PVW_CONFIGURE_MINE(data[0].paraviewConfig);
				}
			} catch (e: any) {
				console.log(e);
			}
		},
		checkForNewBlastdogSelections(
			newData: IGetUiHierarchyResponseDTO[],
			oldData: IGetUiHierarchyResponseDTO[]
		) {
			const bdGroupNew = newData.find(
				(group: IGetUiHierarchyResponseDTO) => group.name === BLASTDOG_DATASET_NAME
			);
			if (!bdGroupNew) return;
			const bdDatasetTypeNew = bdGroupNew.children.find(
				(group: IGetUiHierarchyResponseDTO) => group.name === ("Drillhole" as DatasetType)
			);
			if (!bdDatasetTypeNew?.datasets || !bdDatasetTypeNew?.datasets?.length) return;
			const bdDatasetNew = bdDatasetTypeNew.datasets.find(
				(dataset: IDatasetDocument) => dataset.name === BLASTDOG_DATASET_NAME
			);
			if (!bdDatasetNew) return;

			const bdGroupOld = oldData.find(
				(group: IGetUiHierarchyResponseDTO) => group.name === BLASTDOG_DATASET_NAME
			);
			if (!bdGroupOld) return;
			const bdDatasetTypeOld = bdGroupOld.children.find(
				(group: IGetUiHierarchyResponseDTO) => group.name === ("Drillhole" as DatasetType)
			);
			if (!bdDatasetTypeOld?.datasets || !bdDatasetTypeOld?.datasets?.length) return;
			const bdDatasetOld = bdDatasetTypeOld.datasets.find(
				(dataset: IDatasetDocument) => dataset.name === BLASTDOG_DATASET_NAME
			);
			if (!bdDatasetOld) return;

			let hierarchiesUpdated = false;
			let collarLayerName = "";
			// find new/old hierarchies
			for (const layerName of Object.keys(bdDatasetOld.paraviewLayersInfoSplit)) {
				if (layerName.includes(CollarDrillholeLayerConfigType)) {
					const oldHierarchies =
						bdDatasetOld.paraviewLayersInfoSplit[layerName].hierarchies;
					if (
						bdDatasetNew?.paraviewLayersInfoSplit?.[layerName]?.hierarchies &&
						bdDatasetNew.paraviewLayersInfoSplit[layerName].hierarchies.length >
							oldHierarchies.length
					) {
						hierarchiesUpdated = true;
						collarLayerName = layerName;
					}
				}
			}
			if (!hierarchiesUpdated) return; // stop if no additional hierarchies were added

			// get a list of the newly added hierarchies
			const newHierarchies =
				bdDatasetNew.paraviewLayersInfoSplit[collarLayerName].hierarchies;
			const oldHierarchies =
				bdDatasetOld.paraviewLayersInfoSplit[collarLayerName].hierarchies;
			const addedHierarchies = newHierarchies.filter(
				(item) => !oldHierarchies.includes(item)
			);

			// create hierarchiesDatasetPrefixed, a helper for calling HierarchySelector.deselect
			const datasetVisHierarchies = Object.values(bdDatasetOld.paraviewLayersInfoSplit).find(
				(item) =>
					item.layerConfigType === NonDrillholeLayerConfigType ||
					item.layerConfigType === CollarDrillholeLayerConfigType
			)?.hierarchies;
			if (!datasetVisHierarchies) return;
			const hierarchiesDatasetPrefixed = datasetVisHierarchies.map(
				(item: string) => bdDatasetNew.id + ".." + item
			);

			const updatedHierarchyStates: { [viewId: string]: { selected: string[] } } = JSON.parse(
				JSON.stringify(this.hierarchyStates)
			);
			for (const viewId in this.hierarchyStates) {
				const selectedValues = this.hierarchyStates[viewId].selected;
				let newSelectedValues: string[] = [...selectedValues];
				selectedValues.forEach((value: string) => {
					const splitValues = value.split("..");

					if (splitValues[0] == bdDatasetNew.id) {
						for (const hierarchy of addedHierarchies) {
							// deselect each added hierarchy from this viewId's selections, for each view Id that has a selected blastdog dataset
							newSelectedValues = HierarchySelector.deselect(
								hierarchiesDatasetPrefixed,
								newSelectedValues,
								bdDatasetNew.id + ".." + hierarchy // new hierarchy here (replace root, added backend)
							);
						}
					}
				});

				updatedHierarchyStates[viewId].selected = newSelectedValues;
			}

			this.hierarchyStates = updatedHierarchyStates;
			return false;
		}
	},
	persist: false
});
